import React from 'react';
import styled from 'styled-components';
import { TrackedLink } from 'src/components/TrackedLink';
import Image from 'next/image';
import { useTranslate } from 'react-t7e';
import { media } from 'src/styles/designs';

import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Button } from 'src/components/common/Button';

import notFoundImage from 'src/assets/images/auth-bg.jpg';

import type { NextPage } from 'next';
import { dirRTL } from 'src/styles/theme';
import { VendorGetServerSideProps } from 'vendor/utils/VendorGetServerSideProps';
import { configureSSR } from 'src/utils/configureSSR';

const Wrapper = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: -50px;
  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 0;
    min-height: calc(
      100vh - var(--header-static-height) - var(--header-fixed-height)
    );
  }
`;

const ContentWrapper = styled.div`
  ${media.w.greaterThanOrEqual.desktop} {
    width: 50%;
    height: 100%;
  }
`;

const Content = styled.div`
  max-width: max-content;
  margin: 0 auto;
  padding: 30px 0;
`;

const Title = styled.h1`
  font-weight: 300;
  font-size: 24px;
  line-height: 110%;
  color: ${({ theme }) => theme.text.palette.neauvia};
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 36px;
  }
`;

const Description = styled.p`
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  margin: 16px 0 32px;
  ${media.w.greaterThanOrEqual.desktop} {
    font-size: 16px;
    margin: 20px 0 40px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 370px;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 15% 100%);
  ${media.w.greaterThanOrEqual.desktop} {
    position: absolute;
    bottom: 0;
    top: 0;
    right: 0;
    width: 50%;
    height: calc(100% + var(--footer-static-height));
  }
  ${dirRTL} {
    right: auto;
    left: 0;
    transform: scaleX(-1);
  }
`;

const StyledButton = styled(Button).attrs({ forwardedAs: 'a' })`
  min-width: 158px !important;
  ${media.w.greaterThanOrEqual.desktop} {
    min-width: 264px !important;
  }
`;

const NotFoundPage: NextPage = () => {
  const { _ } = useTranslate();

  return (
    <Wrapper>
      <ConstraintContainer>
        <ContentWrapper>
          <Content>
            <Title>{_('404 Page not found')}</Title>
            <Description>
              {_('Page you were looking for was not found.')}
            </Description>
            <TrackedLink href="/" passHref>
              <StyledButton>{_('Go to main page')}</StyledButton>
            </TrackedLink>
          </Content>
        </ContentWrapper>
      </ConstraintContainer>
      <ImageContainer>
        <Image src={notFoundImage} layout="fill" objectFit="cover" />
      </ImageContainer>
    </Wrapper>
  );
};

export const getServerSideProps = VendorGetServerSideProps((ctx) =>
  configureSSR(ctx, {
    layout: {
      variant: 'default',
      tvbar: true,
    },
    cacheControl: {
      maxAge: 900,
      sMaxAge: 3600,
    },
  }),
);

export default NotFoundPage;
